import React, { useState } from 'react';
import { X, Send, MessageCircle, User, Phone, Loader2 } from 'lucide-react';
import { createTicket } from '../../services/ticketService';

export default function GuestTicketModal({ isOpen, onClose, animal }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Modal açıldığında varsayılan mesajı ayarla
  React.useEffect(() => {
    if (isOpen && animal) {
      setMessage(`Merhaba, ${animal.tagNo} küpe nolu kurbanlıkla ilgileniyorum. Boş hisseler veya fiyatlandırma hakkında bana bilgi verebilir misiniz?`);
      setIsSuccess(false);
    }
  }, [isOpen, animal]);

  if (!isOpen) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const guestPhone = phone.replace(/[^0-9]/g, '');
      const newTicket = {
        animalId: animal.id,
        animalTagNo: animal.tagNo,
        branchId: animal.branch, 
        customerPhone: guestPhone,
        customerName: `${firstName.trim()} ${lastName.trim()} (Misafir)`,
        messages: [{
          senderRole: 'customer',
          senderName: `${firstName.trim()} ${lastName.trim()} (Misafir)`,
          text: message.trim(),
          createdAt: new Date().toISOString()
        }]
      };
      
      await createTicket(newTicket);
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setFirstName('');
        setLastName('');
        setPhone('');
        setMessage('');
        setIsSuccess(false);
      }, 3000);
    } catch (err) {
      alert("Mesaj gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md">
      <div className="bg-navy-900 border border-navy-700 w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-navy-700/50 bg-navy-800/40">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
               <MessageCircle className="w-5 h-5 text-gold-400" />
               Bilgi Al (Ziyaretçi)
            </h3>
            <p className="text-xs text-navy-400 mt-0.5">
               KÜPE: <span className="text-white font-semibold">{animal?.tagNo}</span> ({animal?.branchName || 'Sistem'})
            </p>
          </div>
          {!isSuccess && (
            <button onClick={onClose} className="p-2 text-navy-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 bg-navy-950/30">
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center h-48 text-center animate-in fade-in zoom-in duration-300">
               <div className="w-16 h-16 bg-forest-500/20 text-forest-500 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">✓</span>
               </div>
               <h4 className="text-lg font-bold text-white mb-2">Talebiniz Alındı!</h4>
               <p className="text-navy-300 text-sm max-w-sm">
                  Mesajınız yetkililerimize başarıyla iletildi. Bıraktığınız telefon numarasından en kısa sürede size dönüş yapılacaktır.
               </p>
            </div>
          ) : (
            <form id="guest-ticket-form" onSubmit={handleSend} className="space-y-4">
              <p className="text-sm text-navy-300 mb-4 bg-navy-800/50 p-3 rounded-xl border border-navy-700/50">
                Sistemimize kayıtlı müşteri değilsiniz. Çiftlik yöneticilerimizin size ulaşabilmesi için lütfen bilgilerinizi eksiksiz girin.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-navy-200 mb-1.5 flex items-center gap-1">Adınız</label>
                  <input
                    type="text" required
                    value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    placeholder="örn: Ahmet"
                    className="w-full bg-navy-950 border border-navy-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-gold-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy-200 mb-1.5 flex items-center gap-1">Soyadınız</label>
                  <input
                    type="text" required
                    value={lastName} onChange={(e) => setLastName(e.target.value)}
                    placeholder="örn: Yılmaz"
                    className="w-full bg-navy-950 border border-navy-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-gold-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1.5 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon Numaranız</label>
                <input
                  type="tel" required
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="0555 123 45 67"
                  className="w-full bg-navy-950 border border-navy-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-gold-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1.5">Mesajınız (Değiştirebilirsiniz)</label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-navy-950 border border-navy-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-gold-500 min-h-[100px] resize-none transition-colors"
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!isSuccess && (
          <div className="p-4 border-t border-navy-700/50 bg-navy-800/40">
             <button
                type="submit"
                form="guest-ticket-form"
                disabled={!firstName.trim() || !lastName.trim() || !phone.trim() || !message.trim() || isSubmitting}
                className="w-full py-3 bg-gold-500 text-navy-950 disabled:opacity-50 disabled:cursor-not-allowed font-extrabold rounded-xl transition-transform shadow-[0_0_15px_rgba(234,179,8,0.4)] hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
             >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {isSubmitting ? 'Gönderiliyor...' : 'Talebi İlet'}
             </button>
          </div>
        )}
      </div>
    </div>
  );
}
